'use client'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import React from 'react'

import type { Page } from '@/payload-types'

import { Media } from '@/components/Media'
import { CMSLink } from '@/components/Link'
import { useLenisScroll } from '@/utilities/useLenisScroll'

gsap.registerPlugin(ScrollTrigger)

export const AACHero: React.FC<Page['hero']> = ({
  media,
  foregroundMedia,
  headline,
  text,
  links,
  hasClouds,
  foregroundMediaPosition,
  imageAlignment,
  teaserImage,
  hasTeaser,
}) => {
  const { setHeaderTheme: _setHeaderTheme } = useHeaderTheme()
  const { lenis: _lenis } = useLenisScroll()
  const sectionRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLImageElement>(null)
  const foregroundRef = useRef<HTMLImageElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    const overlay = overlayRef.current
    if (!section || !overlay) return

    gsap.to(overlay, {
      y: 100, // Large parallax distance for debugging
      opacity: 0.8, // Optional: fade clouds slightly
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top', // Start immediately when section is at top
        end: '+=600', // End after scrolling 600px
        scrub: 1,
        // markers: true,
      },
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
  }, [])

  // Combine positioning for images
  const getImagePosition = (horizontal: string, vertical: string) => {
    if (horizontal === 'center' && vertical === 'center') return 'object-center'
    if (horizontal === 'center') return `object-${vertical}`
    if (vertical === 'center') return `object-${horizontal}`
    return `object-${horizontal}-${vertical}`
  }

  const backgroundPosition = getImagePosition(
    imageAlignment ?? 'left',
    foregroundMediaPosition ?? 'top',
  )
  const foregroundPosition = getImagePosition(
    imageAlignment ?? 'left',
    foregroundMediaPosition ?? 'top',
  )

  return (
    <div
      className="relative h-[80vh] overflow-hidden bg-white md:mb-20 md:h-screen md:overflow-auto"
      ref={sectionRef}
    >
      <div className="bg-linear-to-t relative h-[80vh] overflow-hidden from-white from-70% to-blue-100 text-white md:h-screen md:min-h-[800px]">
        {/* Background Image */}
        {media && typeof media === 'object' && (
          <Media
            ref={backgroundRef}
            imgClassName={`h-full w-full object-cover ${backgroundPosition}`}
            priority
            resource={media}
            className="absolute z-10 h-full w-full"
          />
        )}

        <div className="container relative mt-40 flex h-auto w-auto items-start justify-start md:h-2/3 md:px-0 2xl:-left-16 2xl:px-14">
          <div className="relative h-auto w-auto min-w-[50vw] max-w-4xl shadow-2xl md:min-h-[40vh]">
            <div className="absolute bottom-0 left-0 z-20 h-full w-full border-white/30 bg-white/30 text-black shadow-2xl backdrop-blur-md" />
            <div className="relative z-50 h-full w-full">
              <div
                ref={titleRef}
                className="min-h-1/2 relative z-50 h-2/3 max-w-full p-4 text-black md:max-w-3xl md:p-8"
                style={{ zIndex: 50, paddingTop: text ? '' : '10%' }}
              >
                {headline && (
                  <h1
                    className={`  text-black mb-4 ${text ? 'text-h1-m/obile text-xl md:!text-h1 leading-tight' : 'text-4xl md:text-8xl leading-tight max-w-2xl'} z-40`}
                  >
                    {headline}
                  </h1>
                )}
                {text && (
                  <p className="hidden text-lg font-normal leading-relaxed text-black md:block">
                    {text}
                  </p>
                )}
                {Array.isArray(links) && links.length > 0 && (
                  <div className="relative mt-6 flex gap-4" style={{ zIndex: 50 }}>
                    {links.map(({ link }, i) => {
                      return (
                        <CMSLink
                          key={i}
                          {...link}
                          className="rounded-none border border-black bg-accent px-4 py-2 text-black transition-colors hover:bg-gray-200"
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Foreground Image with depth effects */}
        {foregroundMedia && typeof foregroundMedia === 'object' && (
          <Media
            ref={foregroundRef}
            imgClassName={`object-cover h-full w-full ${foregroundPosition}`}
            size="full"
            className="pointer-events-none absolute left-0 top-0 z-40 hidden h-full w-full md:block"
            resource={foregroundMedia}
          />
        )}
      </div>
      {hasClouds && (
        <div
          ref={overlayRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            zIndex: 40,
            pointerEvents: 'none',
            bottom: 0,
          }}
        >
          <Image
            src="/cloud.png"
            alt="AACH Hero Overlay"
            width={2400}
            height={2000}
            className="object-cover object-[center_20%]"
            priority={false}
          />
        </div>
      )}
      {hasTeaser && teaserImage && typeof teaserImage === 'object' && (
        <div className="w-30 h-30 md:w-70 md:h-70 absolute bottom-20 right-20 z-40 hidden max-w-4xl items-center justify-center rounded-full p-8 md:bottom-0 md:flex">
          <Media fill imgClassName="object-cover object-[center_20%]" resource={teaserImage} />
        </div>
      )}
    </div>
  )
}
